import { createContext, type ReactNode, useContext } from "react";
import { cx } from "@/utils/cx";

/**
 * When true, filter controls in the bar should render their disabled styles
 * (`bg-disabled`) rather than relying on an opacity wrapper.
 */
export const TableFilterBarDisabledContext = createContext(false);

/** Whether the surrounding TableFilterBar is in a disabled (empty/loading) state. */
export function useIsTableFilterBarDisabled(): boolean {
	return useContext(TableFilterBarDisabledContext);
}

export const TableFilterBarType = {
	Landing: "landing",
	Workspace: "workspace",
} as const;

export type TableFilterBarType =
	(typeof TableFilterBarType)[keyof typeof TableFilterBarType];

export interface TableFilterBarProps {
	/**
	 * Figma Type variant.
	 * - `landing` — wider search (min 200px)
	 * - `workspace` — denser search (min 150px); room for table options / actions
	 * @default "landing"
	 */
	type?: TableFilterBarType;
	/** Search control (typically a text input with search icon). */
	search: ReactNode;
	/** Filter controls (selects, date pickers, etc.). */
	filters?: ReactNode;
	/**
	 * Trailing actions (e.g. table options, Add).
	 * Figma Workspaces shows table-options (⋯); Landing omits this by default.
	 */
	trailing?: ReactNode;
	className?: string;
}

/**
 * Figma TableFilterBar (3383:31809) — search, filters, and optional trailing actions.
 * Type=Landing Pages | Workspaces.
 * Row counts live on {@link TablePagination}, not in this bar.
 */
export function TableFilterBar({
	type = TableFilterBarType.Landing,
	search,
	filters,
	trailing,
	className,
}: TableFilterBarProps) {
	const isWorkspace = type === TableFilterBarType.Workspace;

	return (
		<div
			className={cx(
				"flex w-full items-center gap-(--spacing-md,8px) overflow-x-auto py-(--spacing-md,8px)",
				className,
			)}
			data-testid="table-filter-bar"
			data-type={type}
		>
			<div
				className={cx(
					"flex-1",
					isWorkspace
						? "min-w-[150px] max-md:min-w-[120px]"
						: "min-w-[200px] max-md:min-w-[120px]",
				)}
			>
				{search}
			</div>

			{filters != null ? (
				<div className="flex shrink-0 items-center gap-(--spacing-md,8px)">
					{filters}
				</div>
			) : null}

			{trailing != null ? (
				<div className="flex shrink-0 items-center gap-(--spacing-md,8px)">
					{trailing}
				</div>
			) : null}
		</div>
	);
}
